/**
 * Where to send the user after they sign in — used to preserve a deep link
 * behind the auth wall (notably the MCP consent page `/mcp/consent?request=…`)
 * across the login round-trip.
 *
 * Stored in sessionStorage rather than React Router's in-memory location state
 * because that state is WIPED by (a) any full page reload and (b) the Firebase
 * auth re-initialisation that gates the whole app on boot (`isInitialised`
 * flips the tree to a SplashScreen and back). Either wipes `location.state`,
 * which is exactly why the consent deep link was losing its `?request=` between
 * the login page and the consent page. sessionStorage survives both and is
 * scoped to the tab/session, so nothing leaks to other tabs or persists past
 * the browser session.
 */
const KEY = 'postLoginRedirect';

/** Remember where to return after login. Best-effort: if storage is unavailable
 *  (private mode, disabled), the caller's history-state fallback still applies. */
export const setPostLoginRedirect = (from: string): void => {
  try {
    sessionStorage.setItem(KEY, from);
  } catch {
    /* storage unavailable — ignore, the history-state path remains */
  }
};

/** Read the stored destination WITHOUT clearing it. Consumption is
 *  arrival-based (see clearPostLoginRedirect): if something else hijacks the
 *  navigation before the user actually reaches the destination, the value
 *  survives for the next attempt — and a peek during render is side-effect
 *  free, so React StrictMode's double-render can't burn it. */
export const peekPostLoginRedirect = (): string | undefined => {
  try {
    return sessionStorage.getItem(KEY) ?? undefined;
  } catch {
    return undefined;
  }
};

/** Clear the stored destination once the user has ARRIVED on it (idempotent).
 *  Called by SignedInGuard when the authenticated user is standing on the
 *  stored path — the one moment we know the deep link was actually honored. */
export const clearPostLoginRedirect = (current: string): void => {
  try {
    if (sessionStorage.getItem(KEY) === current) sessionStorage.removeItem(KEY);
  } catch {
    /* storage unavailable — nothing stored to clear */
  }
};

/** Drop the stored destination outright. Called on logout: signing out makes
 *  AuthGuard re-render unauthenticated on whatever page the user was on, which
 *  stashes that page - so without this the next login in the same tab landed
 *  there instead of the dashboard. */
export const forgetPostLoginRedirect = (): void => {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    /* storage unavailable — nothing stored to forget */
  }
};
